import React, { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { IconAdjustmentsHorizontal, IconX } from '@tabler/icons-react';
import { useMasterData } from '../context/MasterDataContext';

/**
 * Tombol pembuka filter (gaya sama dengan halaman List Barang).
 */
export function FilterToggleButton({ onClick, isOpen = false, activeFilterCount = 0 }) {
  const active = isOpen || activeFilterCount > 0;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-semibold border transition-all shrink-0 cursor-pointer shadow-xs ${
        active
          ? 'bg-amber-500 border-amber-600 text-white shadow-amber-500/20'
          : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:border-amber-300 dark:hover:border-amber-500'
      }`}
      title="Buka Filter"
    >
      <IconAdjustmentsHorizontal size={16} />
      <span className="hidden sm:inline">Filter</span>
      {activeFilterCount > 0 && (
        <span className="px-1.5 py-0.2 text-[10px] font-bold rounded-full leading-tight bg-white text-amber-700">
          {activeFilterCount}
        </span>
      )}
    </button>
  );
}

/**
 * Grid checkbox karat (multi-select, nilai berupa string "16,18").
 */
export function KaratGridFilter({ selectedKarat = '', onChange }) {
  const { karatList = [] } = useMasterData() || {};
  const options = karatList && karatList.length > 0 ? karatList : [16, 17, 18, 19, 20, 21, 22, 23, 24];
  const selected = String(selectedKarat || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

  const toggle = (k) => {
    const s = String(k);
    const next = selected.includes(s) ? selected.filter((x) => x !== s) : [...selected, s];
    onChange(next.join(','));
  };

  return (
    <div className="space-y-1.5">
      {selected.length > 0 && (
        <div className="flex items-center justify-end">
          <button
            type="button"
            onClick={() => onChange('')}
            className="text-xs text-rose-500 hover:text-rose-600 font-bold hover:underline cursor-pointer"
          >
            Reset Karat ({selected.length} dipilih)
          </button>
        </div>
      )}
      <div className="p-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl">
        <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5">Karat</div>
        <div className="grid grid-cols-3 gap-1.5 max-h-36 overflow-y-auto">
          {options.map((k) => {
            const checked = selected.includes(String(k));
            return (
              <label
                key={k}
                className={`flex items-center gap-1.5 p-1.5 rounded-lg border text-xs cursor-pointer transition-all ${
                  checked
                    ? 'bg-amber-50 dark:bg-amber-950/40 border-amber-400 text-amber-800 dark:text-amber-300 font-bold shadow-2xs'
                    : 'border-slate-100 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700/50 text-slate-700 dark:text-slate-300'
                }`}
              >
                <input
                  type="checkbox"
                  className="checkbox checkbox-xs checkbox-warning rounded"
                  checked={checked}
                  onChange={() => toggle(k)}
                />
                <span className="truncate">{k}k</span>
              </label>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/**
 * Drawer filter yang muncul dari sisi kanan layar.
 */
export default function FilterDrawer({
  isOpen,
  onClose,
  title = 'Filter',
  subtitle = 'Filter pencarian',
  activeFilterCount = 0,
  onReset,
  children,
}) {
  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[99999]" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-slate-900/15 dark:bg-black/30 pointer-events-auto" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-hidden pointer-events-none">
          <div className="fixed inset-y-0 right-0 flex max-w-full pl-0 sm:pl-10">
            <Transition.Child
              as={Fragment}
              enter="transform transition ease-in-out duration-300 sm:duration-400"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transform transition ease-in-out duration-300 sm:duration-400"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <Dialog.Panel className="pointer-events-auto w-screen max-w-[340px] sm:max-w-[420px] transform bg-white dark:bg-slate-900 shadow-2xl border-l border-slate-200 dark:border-slate-800 text-left align-middle transition-all flex flex-col h-screen max-h-screen">
                {/* Header */}
                <div className="px-4 py-3.5 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 flex items-center justify-between shrink-0 sticky top-0 z-20">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 rounded-xl bg-amber-500/10 text-amber-600 dark:text-amber-400">
                      <IconAdjustmentsHorizontal size={18} />
                    </div>
                    <div>
                      <Dialog.Title as="h2" className="text-sm font-bold text-slate-800 dark:text-slate-100">
                        {title}
                      </Dialog.Title>
                      <p className="text-[11px] text-slate-500 dark:text-slate-400">
                        {activeFilterCount > 0 ? `${activeFilterCount} kriteria aktif` : subtitle}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={onClose}
                    className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 transition cursor-pointer"
                  >
                    <IconX size={16} />
                  </button>
                </div>

                {/* Body */}
                <div className="p-4 flex-1 bg-slate-50/50 dark:bg-slate-900/50 overflow-y-auto space-y-3.5">
                  {children}
                </div>

                {/* Footer */}
                <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 flex items-center justify-between gap-2 shrink-0 sticky bottom-0 z-20">
                  <div className="text-xs text-slate-500 dark:text-slate-400">
                    {activeFilterCount > 0 ? (
                      <span className="font-semibold text-amber-600 dark:text-amber-400">{activeFilterCount} aktif</span>
                    ) : (
                      'Filter'
                    )}
                  </div>
                  <div className="flex items-center gap-1.5">
                    {activeFilterCount > 0 && onReset && (
                      <button
                        type="button"
                        onClick={onReset}
                        className="px-2.5 py-1.5 text-xs font-semibold text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/30 rounded-xl transition-colors cursor-pointer"
                      >
                        Reset
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={onClose}
                      className="px-3.5 py-1.5 text-xs font-bold bg-amber-500 hover:bg-amber-600 active:scale-95 text-white rounded-xl shadow-sm transition-all cursor-pointer"
                    >
                      Tutup
                    </button>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
